import React, { memo, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./GridProductCard.css";
import { FaHeart, FaRegHeart } from "react-icons/fa6";
import { setWhishlist } from "../../store/slices/whishlistState";
import { showErrorToast } from "../../utils/toast";
import { string } from "../../constants/string";
import { setWhishlistCount } from "../../store/slices/countState";
import { isHoverSupported, RUPEE } from "../../constants/globalConstants";
import { colors } from "../../constants/colors";
import { getPrice, getErrorMessage, getOptimizedUrl } from "../../constants/globalFunction";
import { appApiHandler } from "../../api/appApiHandler";

const GridProductCard = ({ item, gridColumns = 4 }) => {
    const dispatch = useDispatch();
    const [wishlistLoading, setWishlistLoading] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [selectedColorId, setSelectedColorId] = useState(null);
    const [isMobileColors, setIsMobileColors] = useState(
        typeof window !== "undefined" ? window.innerWidth <= 768 : false
    );
    const [dynamicHeight, setDynamicHeight] = useState(320);
    const colorScrollerRef = useRef(null);
    const colorScrollIndexRef = useRef(0);

    // Update dynamic height when gridColumns or window size changes
    useEffect(() => {
        const updateHeight = () => {
            let baseHeight = 320; // Default desktop height
            if (typeof window !== "undefined") {
                if (window.innerWidth <= 480) {
                    baseHeight = 230; // Extra small mobile
                } else if (window.innerWidth <= 768) {
                    baseHeight = 300; // Mobile
                } else if (window.innerWidth >= 1440) {
                    baseHeight = 360; // Large desktop
                }
            }
            
            // On small devices (≤768px), limit to max 3 columns and default to 2
            // If user selects 4, 5, or 6 columns, default to 2 columns
            let effectiveGridColumns = gridColumns;
            if (typeof window !== "undefined" && window.innerWidth <= 768) {
                if (gridColumns >= 4) {
                    effectiveGridColumns = 2; // Apply height multipliers ONLY on small devices
                }
            }

            if (typeof window !== "undefined" && window.innerWidth <= 768) {
                switch (effectiveGridColumns) {
                    case 1:
                        setDynamicHeight(baseHeight * 3); // 3 times height for 1 column
                        break;
                    case 2:
                        setDynamicHeight(baseHeight * 1.2); // 1.2 times height for 2 columns on small devices
                        break;
                    case 3:
                        setDynamicHeight(baseHeight * 1.2); // 1.2 times height for 3 columns on small devices
                        break;
                    case 4:
                    default:
                        setDynamicHeight(baseHeight); // Normal height for 4+ columns
                        break;
                }
            } else {
                switch (effectiveGridColumns) {
                    case 1:
                        setDynamicHeight(baseHeight * 3); // 3 times height for 1 column
                        break;
                    case 2:
                        setDynamicHeight(baseHeight * 2); // 2 times height for 2 columns
                        break;
                    case 3:
                        setDynamicHeight(baseHeight * 1.5); // 1.5 times height for 3 columns
                        break;
                    case 4:
                    default:
                        setDynamicHeight(baseHeight); // Normal height for 4+ columns
                        break;
                }
            }
        };

        updateHeight();
        
        const handleResize = () => {
            setIsMobileColors(window.innerWidth <= 768);
            updateHeight();
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [gridColumns]);

    const { userData } = useSelector((state) => state.userState);
    const { whishlist } = useSelector((state) => state.whishlistState);

    const isWishlisted = whishlist?.some(
        (wishItem) => wishItem?.product_id === item?._id
    );

    useEffect(() => {
        if (item?.colors && item?.colors?.length > 0) {
            setSelectedColorId(item?.colors?.[0]?._id);
        } else {
            setSelectedColorId(null);
        }
    }, [item?._id]);

    useEffect(() => {
        colorScrollIndexRef.current = 0;
        if (colorScrollerRef.current) {
            colorScrollerRef.current.scrollTo({ left: 0 });
        }
    }, [item?.colors?.length, isMobileColors]);

    const getWishlistProducts = async () => {
        const reqData = {
            user_id: userData?._id,
        };
        const response = await appApiHandler.getWishlist(reqData);
        if (response?.status === 200 && response?.data) {
            dispatch(setWhishlist(response?.data));
            dispatch(setWhishlistCount(response?.data?.length));
        }
    };

    const onPress = () => {
        const productUrl = `/product?product_id=${item?._id}`;
        window.open(productUrl, "_blank");
    };

    const onToggleWishlist = async () => {
        if (!userData) {
            showErrorToast(string.error.please_login_to_your_account);
            return;
        }

        const reqData = {
            user_id: userData?._id,
            product_id: item?._id,
        };

        setWishlistLoading(true);
        const response = isWishlisted
            ? await appApiHandler.removeFromWishlist(reqData)
            : await appApiHandler.addToWishlist(reqData);
        setWishlistLoading(false);

        if (response?.status === 200) {
            getWishlistProducts();
        } else if (response?.message) {
            showErrorToast(getErrorMessage(response?.message));
        } else {
            showErrorToast(string.error.failed_due_to_internal_error);
        }
    };

    const getDisplayImages = () => {
        const hasColors = item?.colors && item?.colors?.length > 0;

        if (!hasColors || !selectedColorId) {
            return item?.image || [];
        }

        const colorImages = (item?.image || []).filter(
            (img) => img?.color?._id === selectedColorId
        );

        if (colorImages.length > 0) {
            return colorImages;
        }

        return item?.image || [];
    };

    const getFirstColorImages = () => {
        const hasColors = item?.colors && item?.colors?.length > 0;

        if (!hasColors) {
            return item?.image || [];
        }

        const firstColorId = item?.colors?.[0]?._id;

        const firstColorImages = (item?.image || []).filter(
            (img) => img?.color?._id === firstColorId
        );

        if (firstColorImages.length > 0) {
            return firstColorImages;
        }

        return item?.image || [];
    };

    const displayImages = getDisplayImages();
    const firstColorImages = getFirstColorImages();

    const handleMouseEnter = () => {
        if (!isHoverSupported) return;
        if (firstColorImages?.length > 1) {
            setIsHovered(true);
        } else if (displayImages?.length > 1) {
            setIsHovered(true);
        }
    };

    const handleMouseLeave = () => {
        if (!isHoverSupported) return;
        setIsHovered(false);
    };

    const handleColorClick = (e, colorId) => {
        e.stopPropagation();
        setSelectedColorId(colorId);
        setIsHovered(false);
    };

    const visibleColorCount = isMobileColors ? 2 : 3;
    const swatchStep = 24 + 12;

    useEffect(() => {
        const colorsLength = item?.colors?.length || 0;
        if (!colorScrollerRef.current || colorsLength <= visibleColorCount) {
            return;
        }

        const scroller = colorScrollerRef.current;
        const intervalId = setInterval(() => {
            const maxStartIndex = Math.max(0, colorsLength - visibleColorCount);
            const nextIndex =
                colorScrollIndexRef.current >= maxStartIndex
                    ? 0
                    : colorScrollIndexRef.current + 1;
            colorScrollIndexRef.current = nextIndex;
            scroller.scrollTo({
                left: nextIndex * swatchStep,
                behavior: "smooth",
            });
        }, 4000);

        return () => clearInterval(intervalId);
    }, [visibleColorCount, item?.colors?.length]);

    useEffect(() => {
        const scroller = colorScrollerRef.current;
        if (!scroller) return;

        const onScroll = () => {
            colorScrollIndexRef.current = Math.round(scroller.scrollLeft / swatchStep);
        };

        scroller.addEventListener("scroll", onScroll);
        return () => scroller.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div
            className="gridProductCard"
            onClick={onPress}
            role="button"
            tabIndex={0}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <button
                type="button"
                className={`gridProductCard__wish ${isWishlisted ? "gridProductCard__wish--wishlisted" : ""
                    }`}
                disabled={wishlistLoading}
                aria-pressed={!!isWishlisted}
                onClick={(e) => {
                    e.stopPropagation();
                    onToggleWishlist();
                }}
            >
                {isWishlisted ? (
                    <FaHeart size={15} color={colors.red} />
                ) : (
                    <FaRegHeart size={15} color={colors.primary} />
                )}
            </button>
            {displayImages?.length > 0 ? (
                <div className="gridProductCard__image-container" style={{ height: `${dynamicHeight}px` }}>
                    {item?.on_sale == 1 && (
                        <div className="productCard__sale-badge">Sale</div>
                    )}
                    <img
                        className={`girdProductCard__img ${isHovered &&
                            (firstColorImages?.length > 1 || displayImages?.length > 1)
                            ? "girdProductCard__img--hidden"
                            : ""
                            }`}
                        style={{ height: `${dynamicHeight}px` }}
                        src={getOptimizedUrl(displayImages?.[0]?.image)}
                        alt={item?.name || "product"}
                        loading="lazy"
                    />
                    {
                        isHoverSupported && (
                            firstColorImages?.length > 1 || displayImages?.length > 1) && (
                            <img
                                className={`girdProductCard__img girdProductCard__img--hover ${isHovered ? "girdProductCard__img--visible" : ""
                                    }`}
                                style={{ height: `${dynamicHeight}px` }}
                                src={getOptimizedUrl(
                                    firstColorImages?.length > 1
                                        ? firstColorImages[1]?.image
                                        : displayImages[1]?.image
                                )}
                                alt={item?.name || "product"}
                                loading="lazy"
                            />
                        )
                    }
                </div>
            ) : (
                <div className="girdProductCard__img girdProductCard__img--placeholder" style={{ height: `${dynamicHeight}px` }} />
            )}
            <div className="productCard__content">
                <div className="productCard__name" title={item?.name}>
                    {item?.name}
                </div>
                <div className="productCard__price">
                    {item?.size?.mrp && parseInt(item?.size?.mrp) > 0 && (
                        <span className="productCard__price-mrp">
                            {RUPEE}
                            {item?.size?.mrp}
                        </span>
                    )}
                    <span className="productCard__price-current">
                        {RUPEE}
                        {getPrice(item?.size)}
                    </span>
                </div>
                {item?.colors && item?.colors?.length > 0 && (
                    <div className="productCard__colors-wrapper">
                        <div
                            className={`productCard__colors ${item?.colors?.length <= visibleColorCount
                                ? "productCard__colors--centered"
                                : ""
                                }`}
                            ref={colorScrollerRef}
                        >
                            {item.colors.map((color) => (
                                <div
                                    key={color._id}
                                    className={"productCard__color-swatch"}
                                    style={{ backgroundColor: color.hex_code }}
                                    title={color.name}
                                    onClick={(e) => handleColorClick(e, color._id)}
                                    role="button"
                                    tabIndex={0}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default memo(GridProductCard);
